import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { uploadEvidence, UploadProgress } from "../lib/upload";

const ALLOWED = [
  "image/jpeg", "image/png", "image/webp", "image/heic", "image/heif",
  "video/mp4", "video/quicktime",
  "application/pdf",
];

const LABEL_MAX = 120;

// HEIC/HEIF preview: Safari on iOS/macOS renders these natively, Chrome/
// Firefox don't. Rather than sniff the browser, the FilePreview component
// simply shows an info card for HEIC files — always correct, no false hopes.

export function Upload() {
  const nav = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [visibility, setVisibility] = useState<"public" | "private">("private");
  const [label, setLabel] = useState("");
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState("");

  // Local object URL for the preview. Recomputed whenever the file changes;
  // revoked in the effect cleanup so we don't leak blob URLs.
  const previewUrl = useMemo(
    () => (file ? URL.createObjectURL(file) : null),
    [file],
  );
  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  function onPick(f: File | null) {
    setError("");
    setProgress(null);
    if (!f) { setFile(null); return; }
    if (!ALLOWED.includes(f.type)) {
      setError(`Unsupported file type: ${f.type || "unknown"}`);
      return;
    }
    if (f.size > 100 * 1024 * 1024) {
      setError("File is larger than 100 MB (POC limit).");
      return;
    }
    setFile(f);
  }

  async function onUpload() {
    if (!file) return;
    setError("");
    try {
      const r = await uploadEvidence({
        file, visibility, label,
        onProgress: p => setProgress(p),
      });
      setTimeout(() => nav(`/app/evidence/${r.evidenceId}`), 800);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  const pct = pctFromProgress(progress);
  const busy = !!progress && progress.phase !== "done";

  return (
    <div className="max-w-2xl">
      <h2 className="mb-2">Capture evidence</h2>
      <p className="text-ink-soft text-sm mb-8">
        Photos, videos, or PDFs up to 100 MB. Everything is hashed locally before it leaves
        your device.
      </p>

      {!file ? (
        <div className="space-y-4">
          <button
            className="btn-primary w-full py-6 text-base"
            onClick={() => cameraRef.current?.click()}
          >
            Take a photo or video
          </button>
          <button
            className="btn-secondary w-full py-6 text-base"
            onClick={() => fileRef.current?.click()}
          >
            Choose from device
          </button>
          <input
            ref={cameraRef} type="file" accept="image/*,video/*" capture="environment"
            className="hidden"
            onChange={e => onPick(e.target.files?.[0] ?? null)}
          />
          <input
            ref={fileRef} type="file" accept={ALLOWED.join(",")}
            className="hidden"
            onChange={e => onPick(e.target.files?.[0] ?? null)}
          />
        </div>
      ) : (
        <div className="panel space-y-6">
          {/* Local preview — the file has NOT been uploaded yet. */}
          <FilePreview file={file} url={previewUrl} />

          <div>
            <div className="text-xs uppercase tracking-widest text-ink-soft mb-2">File</div>
            <div className="font-mono text-sm break-all text-ink">{file.name}</div>
            <div className="text-xs text-ink-faint mt-1">
              {formatBytes(file.size)} · {file.type || "unknown"}
            </div>
          </div>

          <div>
            <div className="label flex items-baseline justify-between">
              <span>Label (optional)</span>
              <span className="text-ink-faint normal-case tracking-normal font-normal">
                {label.length}/{LABEL_MAX}
              </span>
            </div>
            <input
              type="text"
              className="input"
              value={label}
              onChange={e => setLabel(e.target.value.slice(0, LABEL_MAX))}
              placeholder="e.g. Storm damage — kitchen roof, 8 Aug 2026"
              maxLength={LABEL_MAX}
              disabled={busy}
            />
            <p className="text-xs text-ink-faint mt-2">
              A short description you can use to find this later. Not included in the sealed
              hash — you can edit or remove it any time.
            </p>
          </div>

          <div>
            <div className="label">Visibility</div>
            <div className="flex gap-2">
              <button
                onClick={() => setVisibility("private")}
                className={`px-4 py-2 text-sm border rounded-md transition-colors ${visibility === "private" ? "border-aigentico-navy text-aigentico-navy bg-white font-semibold" : "border-border text-ink-soft bg-white hover:border-ink-faint"}`}
              >
                Private
              </button>
              <button
                onClick={() => setVisibility("public")}
                className={`px-4 py-2 text-sm border rounded-md transition-colors ${visibility === "public" ? "border-aigentico-navy text-aigentico-navy bg-white font-semibold" : "border-border text-ink-soft bg-white hover:border-ink-faint"}`}
              >
                Public
              </button>
            </div>
            <p className="text-xs text-ink-faint mt-2">
              {visibility === "public"
                ? "Anyone with the link can view the file and its proof."
                : "Only you can view the file. The manifest and proof stay verifiable via the public verifier."}
            </p>
          </div>

          {progress && (
            <div>
              <div className="flex justify-between text-xs text-ink-soft mb-2">
                <span className="uppercase tracking-widest">{phaseLabel(progress.phase)}</span>
                <span className="mono">{pct}%</span>
              </div>
              <div className="h-1.5 bg-border rounded-full overflow-hidden">
                <div className="h-full bg-aigentico-navy transition-all duration-200" style={{ width: `${pct}%` }} />
              </div>
            </div>
          )}

          {error && <div className="text-aigentico-red text-sm border border-aigentico-red bg-white px-4 py-3 rounded-md">{error}</div>}

          <div className="flex gap-3">
            <button
              className="btn-primary flex-1"
              onClick={onUpload}
              disabled={busy}
            >
              {busy ? "Working…" : "Seal it"}
            </button>
            <button
              className="btn-secondary"
              onClick={() => { setFile(null); setLabel(""); setProgress(null); setError(""); }}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Local preview of the picked file. Nothing has been uploaded yet — the src
 * comes from URL.createObjectURL(file) in the parent.
 */
function FilePreview({ file, url }: { file: File; url: string | null }) {
  if (!url) return null;
  const mime = file.type || "";
  const isHeic = mime === "image/heic" || mime === "image/heif";
  const isImage = mime.startsWith("image/") && !isHeic;
  const isVideo = mime.startsWith("video/");
  const isPdf = mime === "application/pdf";

  if (isImage) {
    return (
      <div className="rounded-md border border-border overflow-hidden bg-white flex items-center justify-center max-h-[420px]">
        <img
          src={url}
          alt="Selected file preview"
          className="max-h-[420px] max-w-full object-contain"
        />
      </div>
    );
  }

  if (isVideo) {
    return (
      <div className="rounded-md border border-border overflow-hidden bg-black flex items-center justify-center max-h-[420px]">
        <video
          src={url}
          className="max-h-[420px] max-w-full"
          autoPlay
          muted
          loop
          playsInline
          controls
        />
      </div>
    );
  }

  if (isPdf) {
    return (
      <div className="rounded-md border border-border overflow-hidden bg-white h-[420px]">
        <object
          data={url}
          type="application/pdf"
          className="w-full h-full"
        >
          <div className="p-6 text-sm text-ink-soft">
            Your browser cannot embed this PDF here. The file will still upload
            correctly.
          </div>
        </object>
      </div>
    );
  }

  if (isHeic) {
    return (
      <div className="rounded-md border border-border bg-surface px-4 py-6 text-sm text-ink-soft">
        HEIC preview is not supported in this browser. The file will still upload
        and seal correctly — you can open it after finalisation.
      </div>
    );
  }

  return null;
}

function phaseLabel(p: UploadProgress["phase"]): string {
  switch (p) {
    case "hashing": return "Hashing locally";
    case "initialising": return "Requesting upload URL";
    case "uploading": return "Uploading to staging";
    case "completing": return "Starting finalisation";
    case "done": return "Sealed";
  }
}

function pctFromProgress(p: UploadProgress | null): number {
  if (!p) return 0;
  if (p.phase === "hashing") return Math.round(((p.bytesHashed ?? 0) / p.totalBytes) * 40);
  if (p.phase === "initialising") return 42;
  if (p.phase === "uploading") return 45 + Math.round(((p.bytesUploaded ?? 0) / p.totalBytes) * 45);
  if (p.phase === "completing") return 92;
  return 100;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
